<template>
    <div ref="wrapper" class="ppv2-relative">
        <!-- Button -->
        <button @click="toggle" :class="buttonClass">
            <component :is="icon" :size="17" class="ppv2-flex-shrink-0" :color="iconColor" />

            <span
                v-if="variant === 'cutoff'"
                class="poppinsRegular"
                :class="isDark ? 'cutoff-label-dark' : 'cutoff-label'"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ label }} :&nbsp;<span class="poppinsSemiBold" :class="isDark ? 'cutoff-value-dark' : 'cutoff-value'">{{
                    selectedShortLabel
                }}</span>
            </span>
            <span v-else class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                {{ selectedLabel || label }}
            </span>

            <ChevronDown :size="17" :color="chevronColor" />
        </button>

        <!-- Dropdown -->
        <div
            v-if="open"
            class="menuDropdown ppv2-absolute ppv2-top-full ppv2-left-0 ppv2-overflow-hidden ppv2-z-50 ppv2-min-w-full ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-rounded-xl ppv2-shadow-xl ppv2-border ppv2-border-solid ppv2-border-[#E5E7EB] dark:ppv2-border-gray-700 ppv2-py-1 ppv2-mt-2"
            :class="{ 'menu-border-dark': isDark }"
        >
            <div
                v-if="dropdownTitle"
                class="ppv2-px-4 ppv2-pt-2 ppv2-pb-1 poppinsSemiBold ppv2-text-[#6B7280] dark:ppv2-text-gray-400 ppv2-select-none ppv2-cursor-default"
                :style="{ fontSize: normalizeFontSize(13) }"
            >
                {{ dropdownTitle }}
            </div>
            <button
                v-for="option in options"
                :key="option.value"
                @click="selectOption(option)"
                :class="[
                    'ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-text-black dark:ppv2-text-white ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-3 ppv2-bg-transparent cutoff-option',
                    option.value === internalSelectedValue ? 'poppinsSemiBold' : 'poppinsLight',
                    option.value === internalSelectedValue ? (isDark ? 'active-option-dark' : 'active-option') : '',
                ]"
            >
                <span :style="{ fontSize: normalizeFontSize(14) }">{{ option.label }}</span>
                <Check
                    v-if="option.value === internalSelectedValue"
                    :size="16"
                    :stroke-width="3"
                    class="ppv2-flex-shrink-0"
                    :color="isDark ? '#c398e6' : '#7D2DBD'"
                />
            </button>
            <div
                v-if="dropdownFooter"
                class="ppv2-mx-3 ppv2-mt-1 ppv2-pt-2 ppv2-pb-2 ppv2-border-t ppv2-border-solid ppv2-border-[#E5E7EB] dark:ppv2-border-gray-700 poppinsRegular ppv2-text-[#6B7280] dark:ppv2-text-gray-400 ppv2-select-none ppv2-cursor-default"
                :style="{ fontSize: normalizeFontSize(13) }"
            >
                {{ dropdownFooter }}
            </div>
        </div>
    </div>
</template>

<script>
import { LayoutGrid, ChevronDown, Hash, Funnel, Clock, Check } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'FilterByType',
    components: {
        LayoutGrid,
        ChevronDown,
        Hash,
        Funnel,
        Clock,
        Check,
    },
    props: {
        label: {
            type: String,
            default: 'Filter by type',
        },
        options: {
            type: Array,
            required: true,
        },
        icon: {
            type: [String, Object],
            default: 'LayoutGrid',
        },
        variant: {
            type: String,
            default: 'default',
        },
        dropdownTitle: {
            type: String,
            default: '',
        },
        dropdownFooter: {
            type: String,
            default: '',
        },
        selected: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            open: false,
            selectedValue: this.selected,
        }
    },
    computed: {
        buttonClass() {
            const base =
                'filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all'
            if (this.variant === 'purple') {
                return `${base} filterButton-purple ppv2-bg-[#7D2DBD] hover:ppv2-bg-[#6b26a3] !ppv2-text-white ppv2-border-transparent ppv2-shadow-lg`
            }
            if (this.variant === 'cutoff') {
                return `${base} filterButton-cutoff ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-text-black dark:ppv2-text-white ppv2-border-[#E5E7EB] dark:ppv2-border-gray-700 hover:ppv2-border-[#D1D5DB] hover:ppv2-shadow-sm${
                    this.isDark ? ' cutoff-border-dark' : ''
                }`
            }
            return `${base} ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80`
        },
        iconColor() {
            if (this.variant === 'purple') return '#FFFFFF'
            return this.isDark ? '#FFFFFF' : '#111827'
        },
        chevronColor() {
            if (this.variant === 'purple') return '#FFFFFF'
            if (this.variant === 'cutoff') return this.isDark ? '#9CA3AF' : '#6B7280'
            return this.isDark ? '#FFFFFF' : '#B7B7B7'
        },
        isDark() {
            return this.theme && this.theme.mode === 'dark'
        },
        selectedOption() {
            return this.options.find((o) => o.value === this.internalSelectedValue) || null
        },
        selectedShortLabel() {
            if (!this.selectedOption) return this.label
            return this.selectedOption.shortLabel || this.selectedOption.label
        },
        internalSelectedValue() {
            return this.selected !== null ? this.selected : this.selectedValue
        },
        selectedLabel() {
            const opt = this.options.find((o) => o.value === this.internalSelectedValue)
            return opt ? opt.label : null
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },

    destroyed() {
        document.removeEventListener('click', this.handleClickOutside)
    },

    methods: {
        normalizeFontSize,
        normalizeSize,
        toggle() {
            this.open = !this.open
        },

        select(option) {
            this.$emit('filter-selected', option)
            this.open = false
        },
        handleClickOutside(event) {
            // If click not inside the component, close dropdown
            if (this.$refs.wrapper && !this.$refs.wrapper.contains(event.target)) {
                this.open = false
            }
        },
        selectOption(option) {
            this.selectedValue = option.value
            this.$emit('filter-selected', option.value)
            this.open = false
        },
    },
}
</script>

<style scoped lang="scss">
.dark {
    .filterButton:not(.filterButton-purple):not(.filterButton-cutoff) {
        background-color: #0c0e2f;
    }
    .menuDropdown {
        button.cutoff-option:not(.active-option):not(.active-option-dark) {
            &:hover,
            &:focus,
            &:active {
                background-color: rgba(255, 255, 255, 0.08) !important;
                border-radius: 8px !important;
                color: #fff !important;
            }
        }
    }
}
.filterButton {
    min-height: 40px;
}
.filterButton-cutoff {
    border-width: 1px;
    gap: 8px !important;
    white-space: nowrap;
}
.cutoff-label {
    color: #111827;
    line-height: 1.4;
}
.cutoff-label-dark {
    color: #ffffff;
    line-height: 1.4;
}
.cutoff-value {
    color: #7d2dbd;
}
.cutoff-value-dark {
    color: #c398e6;
}
.cutoff-border-dark {
    border-color: rgba(255, 255, 255, 0.15);
}
.menuDropdown {
    // The global oneui theme forces `.menuDropdown button:hover`
    // (navy background + white label via !important). Override it here:
    // hovered row gets light gray, label keeps its original color,
    // and the selected row always keeps its purple.
    button.cutoff-option {
        &:hover,
        &:focus,
        &:active {
            background-color: #f3f4f6 !important;
            border-radius: 8px !important;
            color: #000 !important;
        }
        &:hover *,
        &:focus *,
        &:active * {
            color: inherit !important;
        }
    }
    button.cutoff-option.active-option {
        &,
        &:hover,
        &:focus,
        &:active {
            background-color: rgba(125, 45, 189, 0.08) !important;
            color: #7d2dbd !important;
        }
    }
    button.cutoff-option.active-option-dark {
        &,
        &:hover,
        &:focus,
        &:active {
            background-color: rgba(195, 152, 230, 0.15) !important;
            color: #c398e6 !important;
        }
    }
}
.menu-border-dark {
    border-color: rgba(255, 255, 255, 0.15);
}
</style>
